import * as React from "react";
import {Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch} from "@mui/material";
import Button from "@mui/material/Button";
import {toast} from "react-toastify";
import PostChecker from "./postChecker";
import {cleanCheckerResolutions, fetchPrCampaignCheckers, updatePrDupe, updatePrScore} from "./portalRedirectCampaignsService";
import PrConfirmDialog from "./prConfirmDialog";
import Loading from "../../components/loading";

type Props = {
    row: any | null;
    kind: "dupe" | "score";
    onClose: () => void;
    onSaved: () => void;
};

const DUPE_TYPES = ["Dupe", "Not-Dupe", "Id"];
const SCORE_TYPES = ["Tier A", "Tier B", "Tier C", "Tier D", "Tier E", "Tier F", "Tier G"];

export default function PrCheckerModal({row, kind, onClose, onSaved}: Props) {
    const [post, setPost] = React.useState<any>(null);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!row) {
            setPost(null);
            setConfirmOpen(false);
            setSaving(false);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setPost(null);
        (async () => {
            const data = await fetchPrCampaignCheckers(row.id);
            if (cancelled) {
                return;
            }
            const src = kind === "dupe" ? data?.dupeChecker : data?.scoreChecker;
            setPost({
                on: !!src?.on,
                url: src?.url || "",
                body: src?.body || "",
                headers: src?.headers || "",
                resolutions: src?.resolutions?.length
                    ? cleanCheckerResolutions(src.resolutions)
                    : [{type: (kind === "dupe" ? DUPE_TYPES : SCORE_TYPES)[0], regex: "missing"}],
            });
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [row, kind]);

    if (!row) {
        return null;
    }

    const save = async () => {
        setSaving(true);
        const checker = {
            on: !!post.on,
            url: post.url,
            body: post.body,
            headers: post.headers,
            resolutions: post.resolutions,
        };
        const ok = kind === "dupe"
            ? await updatePrDupe(row.id, checker)
            : await updatePrScore(row.id, checker);
        if (ok) {
            toast.success(`${kind === "dupe" ? "Dupe" : "Score"} Checker Updates Successfully`);
            setConfirmOpen(false);
            onSaved();
        }
        setSaving(false);
    };

    return (
        <>
        <Dialog open={!!row} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {row.brokerName} - {row.schoolName}: {row.appId} {kind === "dupe" ? "Dupe Checker" : "Score Checker"}
            </DialogTitle>
            <DialogContent>
                {loading || !post ? <Loading/> : (
                    <>
                        <FormControlLabel control={<Switch checked={!!post.on} onChange={(e) => setPost({...post, on: e.target.checked})}/>} label="Active" sx={{mb: 1}}/>
                        <PostChecker post={post} setPost={setPost} resolutionTypes={kind === "dupe" ? DUPE_TYPES : SCORE_TYPES}/>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={() => setConfirmOpen(true)} disabled={loading || !post}>Save</Button>
            </DialogActions>
        </Dialog>
            <PrConfirmDialog
                open={confirmOpen}
                message={`Are you sure you want to save ${kind === "dupe" ? "Dupe" : "Score"} Checker?`}
                loading={saving}
                onClose={() => { if (!saving) setConfirmOpen(false); }}
                onConfirm={save}
            />
        </>
    );
}
